import React from 'react';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AutoAwesome,
  AutoGraph,
  BusinessCenter,
  CheckCircle,
  DataUsage,
  Hub,
  Insights,
  Psychology,
  RocketLaunch,
  SupportAgent,
  TrendingUp,
} from '@mui/icons-material';
import { headerGradient } from '../theme';

const basePath = '/pesquisa-inovacao/solucoes-inovadoras-para-empresas';
const interestHref = 'https://forms.cloud.microsoft/pages/responsepage.aspx?id=VYz8K3TflEuoWng2wpkRuJdblgQ4KAJFvtuXAUjXaxFUMUZFTFdPQUo0MU9NNE5EVDVQVlE5NzY3Ni4u&route=shorturl';

type CardItem = {
  title: string;
  description: string;
  items?: string[];
  icon?: React.ReactNode;
};

type MetricItem = {
  value: string;
  label: string;
};

type ConsultingDetail = {
  title: string;
  eyebrow: string;
  image: string;
  intro: string;
  about?: string;
  chips: string[];
};

const consultingDetails: Record<string, ConsultingDetail> = {
  'agentes-de-inovacao': {
    title: 'Agentes de Inovação',
    eyebrow: 'Transformação organizacional',
    image: '/assets/consultoria/card agente de inovação.png',
    intro:
      'Uma nova forma de pensar e agir: inovação impulsionada por estratégia e aplicada com método.',
    about:
      'Programa de consultoria voltado à transformação organizacional, com foco na formação de colaboradores como agentes de inovação, capazes de impulsionar mudanças, otimizar processos e implementar soluções tecnológicas alinhadas à estratégia do negócio.',
    chips: ['Inovação corporativa', 'Gestão da mudança', 'Cultura inovadora'],
  },
  'empresa-digital': {
    title: 'Empresa Digital',
    eyebrow: 'Transformação digital para empresas',
    image: '/assets/consultoria/card Empresa Digital.png',
    intro:
      'O SENAC Empresa Digital oferece soluções estratégicas para apoiar organizações na jornada de transformação digital, impulsionando vendas, fortalecendo o relacionamento com clientes e modernizando processos com foco em resultados.',
    chips: ['Marketing digital', 'Atendimento', 'IA aplicada'],
  },
  'ia-para-executivos': {
    title: 'Consultoria de Inteligência Artificial para Executivos',
    eyebrow: 'Decisão estratégica com IA',
    image: '/assets/consultoria/card IA para executivos.png',
    intro:
      'Consultoria estratégica voltada a executivos, focada em apoiar a tomada de decisões com o uso da Inteligência Artificial, integrando pensamento estratégico, análise de dados e responsabilidade humana.',
    chips: ['Estratégia', 'Dados', 'Governança'],
  },
  'ia-otimizacao-processos-internos': {
    title: 'IA para Otimização de Processos Internos, Operações e Performance Comercial',
    eyebrow: 'Inovação prática para performance',
    image: '/assets/consultoria/card IA para Otimização de Processos Internos.png',
    intro:
      'Transformamos desafios complexos em soluções acessíveis, garantindo resultados consistentes para pequenas e médias empresas através da inovação prática.',
    chips: ['Processos internos', 'Operações', 'Performance comercial'],
  },
};

const agentesBenefits: CardItem[] = [
  {
    title: 'Otimização de Processos',
    description: 'Redução de ineficiências operacionais por meio da revisão e modernização dos processos internos.',
    icon: <AutoGraph />,
  },
  {
    title: 'Implementação Tecnológica',
    description: 'Adoção estratégica de soluções digitais alinhadas às necessidades e aos objetivos do negócio.',
    icon: <RocketLaunch />,
  },
  {
    title: 'Capacitação de Equipes',
    description: 'Desenvolvimento de competências em inovação, tecnologia e gestão da mudança.',
    icon: <SupportAgent />,
  },
  {
    title: 'Alinhamento Estratégico',
    description: 'Integração entre objetivos organizacionais e iniciativas de inovação em todos os níveis da empresa.',
    icon: <Hub />,
  },
  {
    title: 'Geração de Valor',
    description: 'Criação de novas oportunidades de negócio, aumento de competitividade e crescimento sustentável.',
    icon: <TrendingUp />,
  },
  {
    title: 'Cultura de Inovação',
    description: 'Fortalecimento de uma cultura organizacional orientada à inovação contínua e à adaptação às mudanças.',
    icon: <AutoAwesome />,
  },
];

const empresaDigitalFronts: CardItem[] = [
  {
    title: 'Marketing Digital e Presença Online',
    description:
      'Desenvolvimento de estratégias para fortalecer a marca no ambiente digital, ampliar alcance e gerar resultados comerciais.',
    items: [
      'Planejamento e gestão de campanhas no Google Ads e redes sociais',
      'Criação de artes, conteúdos e identidade visual no Canva',
      'Posicionamento digital para atrair novos clientes',
      'Estratégias de divulgação online para produtos e serviços',
    ],
    icon: <TrendingUp />,
  },
  {
    title: 'Atendimento ao Cliente e Melhoria de Serviços',
    description: 'Ferramentas digitais para aprimorar a experiência do cliente e otimizar o atendimento.',
    items: [
      'Implantação de chatbot no WhatsApp para atendimento automático',
      'Pesquisas de satisfação via Google Forms',
      'Coleta e análise de feedback dos clientes',
      'Mapeamento de oportunidades de melhoria no atendimento',
    ],
    icon: <SupportAgent />,
  },
  {
    title: 'Soluções Inteligentes com Inteligência Artificial',
    description: 'Implementação de tecnologias inovadoras para aumentar eficiência e personalização.',
    items: [
      'Plataformas omnichannel integradas para múltiplos canais de atendimento',
      'IA para análise de comportamento de compra',
      'Previsão de recompra e fidelização de clientes',
      'Personalização de ofertas e interações comerciais',
    ],
    icon: <Psychology />,
  },
  {
    title: 'Consultoria e Capacitação Empresarial',
    description: 'Apoio especializado para empresas que desejam inovar e capacitar suas equipes.',
    items: [
      'Diagnóstico de maturidade digital',
      'Treinamentos corporativos em marketing, vendas e tecnologia',
      'Desenvolvimento de equipes para uso de ferramentas digitais',
      'Planos de inovação para crescimento empresarial',
    ],
    icon: <BusinessCenter />,
  },
];

const iaExecutivosPillars: CardItem[] = [
  {
    title: 'Pensamento Estratégico',
    description:
      'Desenvolvimento de uma visão sistêmica do negócio, conectando objetivos organizacionais, cenários de mercado e oportunidades de inovação para orientar decisões de alto impacto.',
    icon: <Insights />,
  },
  {
    title: 'Análise de Dados',
    description:
      'Uso estruturado de dados para interpretar cenários, identificar padrões e gerar insights estratégicos, ampliando a precisão e a segurança na tomada de decisão.',
    icon: <DataUsage />,
  },
  {
    title: 'Uso prático da Tecnologia',
    description:
      'Aplicação orientada de soluções tecnológicas e Inteligência Artificial nos processos do negócio, com foco em eficiência operacional, produtividade e geração de valor.',
    icon: <Psychology />,
  },
  {
    title: 'Responsabilidade Decisória Humana',
    description:
      'Centralidade do julgamento humano na tomada de decisão, assegurando escolhas éticas, conscientes e alinhadas aos objetivos estratégicos, com uso responsável da tecnologia.',
    icon: <CheckCircle />,
  },
];

const iaExecutivosDeliverables: CardItem[] = [
  {
    title: 'Mapeamento de Maturidade Digital de Empresa',
    description:
      'Avaliação completa do estágio atual da empresa em relação à adoção de tecnologias digitais e IA, identificando lacunas e oportunidades prioritárias.',
    icon: <DataUsage />,
  },
  {
    title: 'Planejamento para Aplicação da IA e Automação',
    description:
      'Identificação e estruturação de um roadmap estratégico para implementação de Inteligência Artificial e automação de processos alinhados aos objetivos do negócio.',
    icon: <RocketLaunch />,
  },
];

const iaExecutivosFronts: CardItem[] = [
  {
    title: 'Fundamentos da IA e Impacto na Gestão',
    description:
      'Introdução à Inteligência Artificial sob a perspectiva da gestão, abordando conceitos essenciais, tendências e impactos nos negócios, além de aspectos éticos e uso responsável da tecnologia.',
    icon: <Psychology />,
  },
  {
    title: 'Aplicações Práticas da IA na Gestão',
    description:
      'Exploração de aplicações práticas da IA no cotidiano organizacional, com foco em produtividade, comunicação, análise de informações e apoio às diferentes áreas do negócio.',
    icon: <AutoAwesome />,
  },
  {
    title: 'IA como Apoio à Tomada de Decisão',
    description:
      'Utilização da Inteligência Artificial como suporte à tomada de decisão, integrando análise de dados, construção de cenários e apoio estratégico ao gestor.',
    icon: <Insights />,
  },
  {
    title: 'Aplicação Organizacional e Governança',
    description:
      'Estruturação do uso da IA nas organizações, com foco em identificação de oportunidades, organização de processos, diretrizes internas e fortalecimento da cultura orientada à inovação.',
    icon: <Hub />,
  },
];

const iterePortfolio: CardItem[] = [
  {
    title: 'Letramento em IA',
    description: 'O comerciante e a equipe entendem de forma prática o que a IA pode fazer pelo negócio deles.',
    icon: <Psychology />,
  },
  {
    title: 'Automação de Processos',
    description: 'Tarefas repetitivas passam a funcionar com automação, governança e suporte adequado.',
    icon: <AutoAwesome />,
  },
  {
    title: 'Dados para Decisão',
    description: 'O comerciante passa a entender os números do próprio negócio para decidir com segurança.',
    icon: <DataUsage />,
  },
  {
    title: 'Canais de Venda',
    description: 'IA nos canais digitais para vender mais e fidelizar clientes de forma automatizada.',
    icon: <TrendingUp />,
  },
];

const itereMethodology: CardItem[] = [
  {
    title: 'Diagnóstico',
    description: 'Levantamento das necessidades reais: onde a empresa perde tempo e onde perde venda.',
  },
  {
    title: 'Solução',
    description: 'Apresentação do caminho tecnológico adequado para o contexto específico do seu negócio.',
  },
  {
    title: 'Consultoria',
    description: 'Apoio direto na implantação até a solução estar funcionando e gerando resultados reais.',
  },
];

const itereMetrics: MetricItem[] = [
  { value: 'Varejo', label: 'Expertise Setorial' },
  { value: 'Direto', label: 'Foco no Ponto' },
  { value: 'AM', label: 'Presença em Manaus' },
  { value: '2021', label: 'Ano de Fundação' },
];

const SectionTitle: React.FC<{ eyebrow?: string; title: string; centered?: boolean; width?: number }> = ({
  eyebrow,
  title,
  centered = false,
  width = 240,
}) => (
  <Box sx={{ textAlign: centered ? 'center' : 'left', mb: 3 }}>
    {eyebrow && (
      <Typography variant="overline" sx={{ color: 'secondary.main', fontWeight: 800, letterSpacing: 1 }}>
        {eyebrow}
      </Typography>
    )}
    <Typography
      variant="h4"
      sx={{
        color: 'primary.main',
        fontWeight: 800,
        lineHeight: 1.18,
        position: 'relative',
        pb: 1.2,
        '&::after': {
          content: '""',
          position: 'absolute',
          left: centered ? '50%' : 0,
          transform: centered ? 'translateX(-50%)' : 'none',
          bottom: 0,
          width,
          height: 3,
          bgcolor: 'secondary.main',
        },
      }}
    >
      {title}
    </Typography>
  </Box>
);

const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
  <Box component="ul" sx={{ m: 0, pl: 2.4 }}>
    {items.map((item) => (
      <Typography component="li" key={item} sx={{ color: 'text.secondary', lineHeight: 1.75, mb: 0.6 }}>
        {item}
      </Typography>
    ))}
  </Box>
);

const DetailCards: React.FC<{ items: CardItem[]; columns?: 3 | 4 | 6 }> = ({ items, columns = 3 }) => {
  const theme = useTheme();
  const md = columns === 4 ? 3 : columns === 6 ? 6 : 4;

  return (
    <Grid container spacing={2.5}>
      {items.map((item) => (
        <Grid item xs={12} sm={6} md={md} key={item.title}>
          <Card
            variant="outlined"
            sx={{
              height: '100%',
              borderColor: alpha(theme.palette.primary.main, 0.1),
              boxShadow: '0 10px 24px rgba(15, 23, 42, 0.05)',
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 3.2 } }}>
              <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.2 }}>
                {item.icon && <Box sx={{ color: 'secondary.main', display: 'grid', placeItems: 'center' }}>{item.icon}</Box>}
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800, lineHeight: 1.25 }}>
                  {item.title}
                </Typography>
              </Stack>
              <Typography sx={{ color: 'text.secondary', lineHeight: 1.75 }}>{item.description}</Typography>
              {item.items && (
                <>
                  <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 800, mt: 2, mb: 1 }}>
                    Opções de serviços
                  </Typography>
                  <BulletList items={item.items} />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

const ConsultingDetailPage: React.FC = () => {
  const { consultoriaId } = useParams();
  const theme = useTheme();
  const detail = consultoriaId ? consultingDetails[consultoriaId] : undefined;

  if (!detail || !consultoriaId) {
    return <Navigate to={basePath} replace />;
  }

  const isAgentes = consultoriaId === 'agentes-de-inovacao';
  const isEmpresaDigital = consultoriaId === 'empresa-digital';
  const isIaExecutivos = consultoriaId === 'ia-para-executivos';
  const isItere = consultoriaId === 'ia-otimizacao-processos-internos';

  return (
    <Box sx={{ pt: { xs: 5, md: 9 }, pb: 8, backgroundColor: '#f7f8fb' }}>
      <Box
        sx={{
          mx: { xs: -2, md: -4 },
          mt: { xs: -5, md: -9 },
          px: { xs: 2, md: 8 },
          py: { xs: 5, md: 7 },
          background: headerGradient,
          color: '#fff',
        }}
      >
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={7}>
            <Button component={RouterLink} to={basePath} color="inherit" variant="outlined" sx={{ mb: 3, fontWeight: 700 }}>
              Voltar para consultorias
            </Button>
            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.78)', fontWeight: 800, letterSpacing: 1 }}>
              {detail.eyebrow}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.12, mt: 0.5 }}>
              {detail.title}
            </Typography>
            <Typography sx={{ mt: 2.4, color: 'rgba(255,255,255,0.9)', lineHeight: 1.85, maxWidth: 840 }}>
              {detail.intro}
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2.5 }}>
              {detail.chips.map((chip) => (
                <Chip
                  key={chip}
                  label={chip}
                  variant="outlined"
                  sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.42)' }}
                />
              ))}
            </Stack>
            <Button
              component="a"
              href={interestHref}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              color="secondary"
              sx={{ mt: 3, fontWeight: 800 }}
            >
              Registre seu interesse
            </Button>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box
              sx={{
                minHeight: { xs: 260, md: 390 },
                backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.12), rgba(0,0,0,0.28)), url("${detail.image}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                boxShadow: '0 24px 52px rgba(0,0,0,0.24)',
              }}
              role="img"
              aria-label={detail.title}
            />
          </Grid>
        </Grid>
      </Box>

      {isAgentes && (
        <>
          <Box component="section" sx={{ mt: 7 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <SectionTitle eyebrow="Sobre" title="Agentes de Inovação" width={190} />
                <Typography sx={{ color: 'text.secondary', lineHeight: 1.85 }}>
                  {detail.about ?? detail.intro}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%', borderColor: alpha(theme.palette.primary.main, 0.1) }}>
                  <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <SectionTitle eyebrow="Metodologia" title="Metodologia do Programa" width={250} />
                    <Typography sx={{ color: 'text.secondary', lineHeight: 1.85 }}>
                      Abordagem estruturada de consultoria que conduz a organização desde o diagnóstico da maturidade em
                      inovação até a consolidação de uma cultura inovadora, com foco em resultados concretos, aplicação
                      prática e evolução contínua.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          <Box component="section" sx={{ mt: 7 }}>
            <SectionTitle eyebrow="Benefícios" title="Benefícios para sua organização" centered width={310} />
            <DetailCards items={agentesBenefits} columns={3} />
          </Box>
        </>
      )}

      {isEmpresaDigital && (
        <>
          <Box component="section" sx={{ mt: 7 }}>
            <SectionTitle eyebrow="Sobre" title="Empresa Digital" width={180} />
            <Typography sx={{ color: 'text.secondary', lineHeight: 1.85, maxWidth: 920 }}>{detail.intro}</Typography>
          </Box>

          <Box component="section" sx={{ mt: 7 }}>
            <SectionTitle eyebrow="Frentes de Atuação" title="Soluções para a jornada digital" centered width={320} />
            <DetailCards items={empresaDigitalFronts} columns={4} />
          </Box>
        </>
      )}

      {isIaExecutivos && (
        <>
          <Box component="section" sx={{ mt: 7 }}>
            <SectionTitle eyebrow="Sobre" title="Inteligência Artificial para Executivos" width={360} />
            <Typography sx={{ color: 'text.secondary', lineHeight: 1.85, maxWidth: 920 }}>{detail.intro}</Typography>
          </Box>

          <Box component="section" sx={{ mt: 7 }}>
            <SectionTitle eyebrow="Pilares do Programa" title="Base estratégica da consultoria" centered width={320} />
            <DetailCards items={iaExecutivosPillars} columns={4} />
          </Box>

          <Box component="section" sx={{ mt: 7 }}>
            <SectionTitle eyebrow="Soluções a serem entregues" title="Entregas principais" centered width={240} />
            <DetailCards items={iaExecutivosDeliverables} columns={6} />
          </Box>

          <Box component="section" sx={{ mt: 7 }}>
            <SectionTitle eyebrow="Frentes de Atuação" title="Aplicação da IA na gestão" centered width={280} />
            <DetailCards items={iaExecutivosFronts} columns={4} />
          </Box>

          <Card sx={{ mt: 7, color: '#fff', background: headerGradient, boxShadow: '0 24px 50px rgba(0, 58, 112, 0.18)' }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    Pronto para liderar com Inteligência Artificial?
                  </Typography>
                  <Typography sx={{ mt: 1.4, color: 'rgba(255,255,255,0.88)', lineHeight: 1.75 }}>
                    Entre em contato e descubra como este programa pode transformar a capacidade decisória da sua equipe
                    executiva.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    component="a"
                    href={interestHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    color="secondary"
                    fullWidth
                    sx={{ py: 1.35, fontWeight: 800 }}
                  >
                    Registre seu interesse
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}

      {isItere && (
        <>
          <Box component="section" sx={{ mt: 7 }}>
            <Grid container spacing={4} alignItems="stretch">
              <Grid item xs={12} md={7}>
                <SectionTitle eyebrow="Sobre" title="Direto ao ponto, com foco em resultados" width={360} />
                <Typography sx={{ color: 'text.secondary', lineHeight: 1.85 }}>
                  Consultoria focada em soluções estratégicas para apoiar a jornada de transformação digital das
                  organizações. Visa impulsionar vendas, fortalecer o relacionamento com clientes e modernizar processos
                  (incluindo a implementação de agentes de IA para vendas, rotinas internas e chatbots).
                </Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <Card
                  sx={{
                    height: '100%',
                    color: '#fff',
                    background: headerGradient,
                    boxShadow: '0 18px 38px rgba(0, 58, 112, 0.18)',
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.5 }}>
                      “Transforme desafios complexos em soluções acessíveis, garantindo resultados consistentes e parcerias
                      duradouras.”
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          <Box component="section" sx={{ mt: 7 }}>
            <SectionTitle eyebrow="Pilares de Inovação" title="Nosso Portfólio de Serviços" centered width={300} />
            <Typography sx={{ textAlign: 'center', color: 'text.secondary', maxWidth: 780, mx: 'auto', mb: 4 }}>
              Soluções desenhadas para micro e pequenas empresas dos setores de Comércio, Turismo, Hospitalidade e Serviços.
            </Typography>
            <DetailCards items={iterePortfolio} columns={4} />
          </Box>

          <Box component="section" sx={{ mt: 7 }}>
            <SectionTitle eyebrow="Como Trabalhamos" title="Metodologia de Entrega" centered width={270} />
            <Typography sx={{ textAlign: 'center', color: 'text.secondary', maxWidth: 760, mx: 'auto', mb: 4 }}>
              Um processo transparente onde o diagnóstico é sempre o ponto de partida.
            </Typography>
            <Grid container spacing={2.5}>
              {itereMethodology.map((step, index) => (
                <Grid item xs={12} md={4} key={step.title}>
                  <Card variant="outlined" sx={{ height: '100%', borderColor: alpha(theme.palette.primary.main, 0.1) }}>
                    <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          display: 'grid',
                          placeItems: 'center',
                          background: headerGradient,
                          color: '#fff',
                          fontWeight: 800,
                          fontSize: '1.2rem',
                          mb: 2,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800 }}>
                        {step.title}
                      </Typography>
                      <Typography sx={{ mt: 1, color: 'text.secondary', lineHeight: 1.75 }}>{step.description}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Grid container spacing={2} sx={{ mt: 5 }}>
            {itereMetrics.map((metric) => (
              <Grid item xs={12} sm={6} md={3} key={metric.label}>
                <Card variant="outlined" sx={{ borderColor: alpha(theme.palette.secondary.main, 0.18), textAlign: 'center' }}>
                  <CardContent>
                    <Typography variant="h4" sx={{ color: 'secondary.main', fontWeight: 900 }}>
                      {metric.value}
                    </Typography>
                    <Typography sx={{ mt: 0.6, color: 'text.secondary', fontWeight: 700 }}>{metric.label}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Card sx={{ mt: 7, color: '#fff', background: headerGradient, boxShadow: '0 24px 50px rgba(0, 58, 112, 0.18)' }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    Pronto para otimizar seus resultados?
                  </Typography>
                  <Typography sx={{ mt: 1.4, color: 'rgba(255,255,255,0.88)', lineHeight: 1.75 }}>
                    Agende um diagnóstico inicial com a Itere e descubra como a Inteligência Artificial pode transformar sua
                    operação comercial.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    component="a"
                    href={interestHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    color="secondary"
                    fullWidth
                    sx={{ py: 1.35, fontWeight: 800 }}
                  >
                    Solicitar Contato Comercial
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}

      <Divider sx={{ my: 7 }} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
        <Button component={RouterLink} to={basePath} variant="outlined" color="primary" sx={{ fontWeight: 800 }}>
          Ver todas as consultorias
        </Button>
        <Button
          component="a"
          href={interestHref}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          color="secondary"
          sx={{ fontWeight: 800 }}
        >
          Registre seu interesse
        </Button>
      </Stack>
    </Box>
  );
};

export default ConsultingDetailPage;
